import { AlertTriangle, BadgeCheck, CircleDashed, FileWarning, Sparkles, UserCheck } from 'lucide-react'
import type { ReactNode } from 'react'

import type { AssetReview, AssetStatus } from '@/api/assets'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { WithTooltip } from '@/components/ui/tooltip'
import { cn } from '@/components/ui/utils'
import { formatPct } from '@/lib/format'
import type { PublishedTier, ReviewStatus } from '@/schema'

/**
 * The one place the library says what state an asset's annotation is in.
 *
 * Three facts, three shapes, so a glance is enough:
 *  - **review status** (seed / proposed / draft / reviewed / published) as a
 *    word, because the operator sorts by it;
 *  - **published tier** (`auto` = bronze, `reviewed` = human-checked) as a
 *    coloured ribbon — the difference decides whether a story may use it;
 *  - **confidence** and **lint errors** as small trailing chips.
 *
 * Every label is a plain sentence in its tooltip: this screen is also read
 * by people who did not write the spec.
 */

const STATUS_LABEL: Record<ReviewStatus, string> = {
  seed: 'Seed',
  proposed: 'AI proposed',
  draft: 'Draft',
  reviewed: 'Reviewed',
  published: 'Published',
}

const STATUS_TONE: Record<ReviewStatus, BadgeTone> = {
  seed: 'neutral',
  proposed: 'info',
  draft: 'warn',
  reviewed: 'ok',
  published: 'ok',
}

const STATUS_HINT: Record<ReviewStatus, string> = {
  seed: 'Read straight from the bucket sidecar — nobody has checked it yet.',
  proposed: 'Claude suggested changes that are waiting for review.',
  draft: 'Edited but not published; stories will not use it yet.',
  reviewed: 'A person checked this and marked it good.',
  published: 'Stories can use this.',
}

const STATUS_ICON: Record<ReviewStatus, ReactNode> = {
  seed: <CircleDashed aria-hidden />,
  proposed: <Sparkles aria-hidden />,
  draft: <FileWarning aria-hidden />,
  reviewed: <UserCheck aria-hidden />,
  published: <BadgeCheck aria-hidden />,
}

export const TIER_LABEL: Record<PublishedTier, string> = { auto: 'Auto', reviewed: 'Checked' }
const TIER_HINT: Record<PublishedTier, string> = {
  auto: 'Published automatically because it had no errors. Good enough to make a story with.',
  reviewed: 'Published after a person checked it.',
}

export interface StatusBadgeProps {
  review: AssetReview
  /** The asset's own file status; `broken`/`missing` outranks anything else. */
  assetStatus?: AssetStatus
  /** Hide the confidence chip in dense rows. */
  showConfidence?: boolean
  className?: string
}

export function StatusBadge({ review, assetStatus, showConfidence = true, className }: StatusBadgeProps) {
  const status = review.status
  const tier = review.published_tier

  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1', className)}>
      {assetStatus && assetStatus !== 'ok' ? (
        <WithTooltip label={ASSET_STATUS_HINT[assetStatus]}>
          <Badge tone="danger" className="gap-1 [&_svg]:size-3.5">
            <AlertTriangle aria-hidden />
            {ASSET_STATUS_LABEL[assetStatus]}
          </Badge>
        </WithTooltip>
      ) : null}

      <WithTooltip label={STATUS_HINT[status]}>
        <Badge tone={STATUS_TONE[status]} className="gap-1 [&_svg]:size-3.5">
          {STATUS_ICON[status]}
          {STATUS_LABEL[status]}
        </Badge>
      </WithTooltip>

      {tier ? (
        <WithTooltip label={TIER_HINT[tier]}>
          <Badge tone={tier}>{TIER_LABEL[tier]}</Badge>
        </WithTooltip>
      ) : null}

      {review.lint_errors > 0 ? (
        <WithTooltip label={`${review.lint_errors} problem${review.lint_errors === 1 ? '' : 's'} must be fixed before this can be published.`}>
          <Badge tone="danger">{review.lint_errors} to fix</Badge>
        </WithTooltip>
      ) : null}

      {review.pending_proposals > 0 ? (
        <WithTooltip label="Claude has suggestions waiting in Review.">
          <Badge tone="info">{review.pending_proposals} waiting</Badge>
        </WithTooltip>
      ) : null}

      {showConfidence && review.confidence != null ? (
        <WithTooltip label="How sure the source of this annotation was. Low numbers are worth a look.">
          <Badge tone={review.confidence >= 0.75 ? 'neutral' : 'warn'}>{formatPct(review.confidence, { digits: 0 })}</Badge>
        </WithTooltip>
      ) : null}
    </span>
  )
}

const ASSET_STATUS_LABEL: Record<Exclude<AssetStatus, 'ok'>, string> = {
  broken: 'Broken',
  missing: 'Missing',
  duplicate: 'Duplicate',
  too_large: 'Too large',
}

const ASSET_STATUS_HINT: Record<Exclude<AssetStatus, 'ok'>, string> = {
  broken: 'The files for this asset do not fit together — a sheet without its atlas, or an atlas larger than its sheet.',
  missing: 'The bucket no longer has these files. It was probably renamed.',
  duplicate: 'Another folder holds the same art; this one is the spare.',
  too_large: 'Bigger than the studio can re-pack in one piece; it is served in tiles.',
}

/** A one-word ribbon for the corner of a card (grid view). */
export function StatusRibbon({ review, assetStatus }: { review: AssetReview; assetStatus: AssetStatus }) {
  if (assetStatus !== 'ok') {
    return (
      <span className="rounded-full bg-danger px-2 py-0.5 text-xs font-semibold text-white shadow-card">{ASSET_STATUS_LABEL[assetStatus]}</span>
    )
  }
  if (review.published_tier) {
    return (
      <span
        className={cn(
          'rounded-full px-2 py-0.5 text-xs font-semibold shadow-card',
          review.published_tier === 'reviewed' ? 'bg-tier-reviewed text-white' : 'bg-tier-auto text-white',
        )}
      >
        {TIER_LABEL[review.published_tier]}
      </span>
    )
  }
  return <span className="rounded-full bg-paper px-2 py-0.5 text-xs font-semibold text-ink-2 shadow-card">{STATUS_LABEL[review.status]}</span>
}
